import * as THREE from "three/webgpu";
import type { EdgeSpatialProjection } from "../../domain/mesh";
import { CARTOGRAPHY_NORMAL, projectVectorToCartographyPlane } from "../config/worldFrame";
import type { SpatialProjectionPipeline } from "../spatial/SpatialProjectionPipeline";

const PATH_SEGMENTS = 36;
const PATH_POINT_COUNT = PATH_SEGMENTS + 1;
const PATH_EPSILON = 0.000001;

export type RelationPath = {
  semanticPoints: THREE.Vector3[];
  semanticArcLengths: Float64Array;
  semanticLength: number;
  opticalPointBlend: Float32Array;
};

export type RelationEdgeRuntime = {
  path: RelationPath;
  geometry: THREE.BufferGeometry;
  line: THREE.Line;
  material: THREE.LineBasicMaterial;
  positions: Float32Array;
  positionAttribute: THREE.BufferAttribute;
  visualPoint: THREE.Vector3;
  baseOpacity: number;
};

function smootherstep01(value: number) {
  const t = THREE.MathUtils.clamp(value, 0, 1);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function opticalEndpointBlend(value: number) {
  const cosine = Math.cos(Math.PI * THREE.MathUtils.clamp(value, 0, 1));
  return cosine * cosine;
}

function createPathStorage() {
  const semanticPoints = Array.from({ length: PATH_POINT_COUNT }, () => new THREE.Vector3());
  const semanticArcLengths = new Float64Array(PATH_POINT_COUNT);
  const opticalPointBlend = new Float32Array(PATH_POINT_COUNT);
  for (let index = 0; index < PATH_POINT_COUNT; index += 1) {
    const pathT = index / PATH_SEGMENTS;
    opticalPointBlend[index] = opticalEndpointBlend(pathT);
  }
  return { semanticPoints, semanticArcLengths, opticalPointBlend };
}

export class RelationPathProjector {
  private readonly midpoint = new THREE.Vector3();
  private readonly span = new THREE.Vector3();
  private readonly radial = new THREE.Vector3();
  private readonly planarSpan = new THREE.Vector3();
  private readonly perpendicular = new THREE.Vector3();
  private readonly offset = new THREE.Vector3();
  private readonly lift = new THREE.Vector3();
  private readonly controlA = new THREE.Vector3();
  private readonly controlB = new THREE.Vector3();
  private readonly sourceCorrection = new THREE.Vector3();
  private readonly targetCorrection = new THREE.Vector3();

  constructor(private readonly spatial: SpatialProjectionPipeline) {}

  createPath(
    sourceAuthoring: THREE.Vector3,
    targetAuthoring: THREE.Vector3,
    sourceSemantic: THREE.Vector3,
    targetSemantic: THREE.Vector3,
    route: EdgeSpatialProjection,
  ): RelationPath {
    const storage = createPathStorage();
    const path: RelationPath = {
      semanticPoints: storage.semanticPoints,
      semanticArcLengths: storage.semanticArcLengths,
      semanticLength: 0,
      opticalPointBlend: storage.opticalPointBlend,
    };
    this.syncPath(path, sourceAuthoring, targetAuthoring, sourceSemantic, targetSemantic, route);
    return path;
  }

  syncPath(
    path: RelationPath,
    sourceAuthoring: THREE.Vector3,
    targetAuthoring: THREE.Vector3,
    sourceSemantic: THREE.Vector3,
    targetSemantic: THREE.Vector3,
    route: EdgeSpatialProjection,
  ) {
    this.prepareAuthoringCurveControls(sourceAuthoring, targetAuthoring, route);
    const lastIndex = path.semanticPoints.length - 1;

    for (let index = 0; index <= lastIndex; index += 1) {
      this.sampleAuthoringCurvePoint(sourceAuthoring, targetAuthoring, index / lastIndex, path.semanticPoints[index]);
      this.spatial.toSemantic(path.semanticPoints[index], path.semanticPoints[index], "connector");
    }

    this.sourceCorrection.copy(sourceSemantic).sub(path.semanticPoints[0]);
    this.targetCorrection.copy(targetSemantic).sub(path.semanticPoints[lastIndex]);

    for (let index = 0; index <= lastIndex; index += 1) {
      const pathT = lastIndex > 0 ? index / lastIndex : 0;
      const boundaryMix = smootherstep01(pathT);
      path.semanticPoints[index]
        .addScaledVector(this.sourceCorrection, 1 - boundaryMix)
        .addScaledVector(this.targetCorrection, boundaryMix);
    }

    path.semanticPoints[0].copy(sourceSemantic);
    path.semanticPoints[lastIndex].copy(targetSemantic);
    path.semanticLength = this.measureSemanticPath(path.semanticPoints, path.semanticArcLengths);
  }

  createEdge(path: RelationPath, color: string, baseOpacity: number): RelationEdgeRuntime {
    const positions = new Float32Array(path.semanticPoints.length * 3);
    const geometry = new THREE.BufferGeometry();
    const positionAttribute = new THREE.BufferAttribute(positions, 3);
    positionAttribute.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute("position", positionAttribute);
    const material = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: baseOpacity,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });
    const line = new THREE.Line(geometry, material);
    line.frustumCulled = false;
    return {
      path,
      geometry,
      line,
      material,
      positions,
      positionAttribute,
      visualPoint: new THREE.Vector3(),
      baseOpacity,
    };
  }

  updateVisual(
    runtime: RelationEdgeRuntime,
    singularityPosition: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
  ) {
    if (!runtime.line.visible) return;
    for (let index = 0; index < runtime.path.semanticPoints.length; index += 1) {
      this.spatial.toVisualBlended(
        runtime.path.semanticPoints[index],
        singularityPosition,
        camera,
        viewportWidth,
        viewportHeight,
        runtime.visualPoint,
        "connector",
        "point",
        runtime.path.opticalPointBlend[index],
      );
      const offset = index * 3;
      runtime.positions[offset] = runtime.visualPoint.x;
      runtime.positions[offset + 1] = runtime.visualPoint.y;
      runtime.positions[offset + 2] = runtime.visualPoint.z;
    }
    runtime.positionAttribute.needsUpdate = true;
  }

  sampleSemantic(path: RelationPath, progress: number, target: THREE.Vector3) {
    if (path.semanticPoints.length < 2 || path.semanticLength <= PATH_EPSILON) {
      target.copy(path.semanticPoints[0]);
      return path.opticalPointBlend[0] ?? 1;
    }

    if (progress <= 0) {
      target.copy(path.semanticPoints[0]);
      return path.opticalPointBlend[0] ?? 1;
    }
    const lastIndex = path.semanticPoints.length - 1;
    if (progress >= 1) {
      target.copy(path.semanticPoints[lastIndex]);
      return path.opticalPointBlend[lastIndex] ?? 1;
    }

    const targetDistance = progress * path.semanticLength;
    let low = 1;
    let high = path.semanticArcLengths.length - 1;
    while (low < high) {
      const middle = Math.floor((low + high) * 0.5);
      if (path.semanticArcLengths[middle] < targetDistance) low = middle + 1;
      else high = middle;
    }

    const upperIndex = low;
    const lowerIndex = Math.max(upperIndex - 1, 0);
    const lowerDistance = path.semanticArcLengths[lowerIndex];
    const segmentLength = Math.max(path.semanticArcLengths[upperIndex] - lowerDistance, PATH_EPSILON);
    const localT = THREE.MathUtils.clamp((targetDistance - lowerDistance) / segmentLength, 0, 1);
    target.lerpVectors(path.semanticPoints[lowerIndex], path.semanticPoints[upperIndex], localT);
    return THREE.MathUtils.lerp(path.opticalPointBlend[lowerIndex], path.opticalPointBlend[upperIndex], localT);
  }

  private measureSemanticPath(points: THREE.Vector3[], target: Float64Array) {
    target[0] = 0;
    let semanticLength = 0;
    for (let index = 1; index < points.length; index += 1) {
      semanticLength += points[index - 1].distanceTo(points[index]);
      target[index] = semanticLength;
    }
    return semanticLength;
  }

  private prepareAuthoringCurveControls(source: THREE.Vector3, target: THREE.Vector3, route: EdgeSpatialProjection) {
    this.midpoint.copy(source).add(target).multiplyScalar(0.5);
    this.span.copy(target).sub(source);
    const distance = this.span.length();
    projectVectorToCartographyPlane(this.midpoint, this.radial);
    projectVectorToCartographyPlane(this.span, this.planarSpan);
    if (this.radial.lengthSq() < 0.0001) this.radial.crossVectors(CARTOGRAPHY_NORMAL, this.planarSpan);
    this.radial.normalize();
    this.perpendicular.crossVectors(CARTOGRAPHY_NORMAL, this.planarSpan).normalize();
    this.offset
      .copy(this.radial)
      .multiplyScalar(distance * route.bend)
      .addScaledVector(this.perpendicular, distance * route.skew);
    this.lift.copy(CARTOGRAPHY_NORMAL).multiplyScalar(route.lift);
    this.controlA.copy(source).lerp(target, 0.29).addScaledVector(this.offset, 0.82).add(this.lift);
    this.controlB.copy(source).lerp(target, 0.71).addScaledVector(this.offset, 0.94).add(this.lift);
  }

  private sampleAuthoringCurvePoint(source: THREE.Vector3, target: THREE.Vector3, t: number, targetPoint: THREE.Vector3) {
    const inverse = 1 - t;
    const inverse2 = inverse * inverse;
    const t2 = t * t;
    return targetPoint
      .set(0, 0, 0)
      .addScaledVector(source, inverse2 * inverse)
      .addScaledVector(this.controlA, 3 * inverse2 * t)
      .addScaledVector(this.controlB, 3 * inverse * t2)
      .addScaledVector(target, t2 * t);
  }
}

